import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Derived states for easy access
  const isExpired = subscriptionStatus?.is_expired || false;
  const isGracePeriod = subscriptionStatus?.is_grace_period || false;
  const isActive = subscriptionStatus?.status === 'active';
  const canWrite = !isExpired; // Can write if not fully expired (grace period still allows writes)
  const daysRemaining = subscriptionStatus?.days_remaining;

  const fetchSubscriptionStatus = useCallback(async () => {
    // Only fetch for Admin and GroupAdmin users
    if (user?.role !== 'Admin' && user?.role !== 'GroupAdmin') {
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Use different endpoint for GroupAdmin
      const endpoint = user?.role === 'GroupAdmin' 
        ? `${API_URL}/api/groups/my-subscription`
        : `${API_URL}/api/subscriptions/my-subscription`;

      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data);
        setError(null);
      } else {
        console.error('Failed to fetch subscription status:', response.status);
      }
    } catch (err) {
      console.error('Error fetching subscription status:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  // Refresh subscription status (can be called manually)
  const refreshStatus = () => {
    setIsLoading(true);
    fetchSubscriptionStatus();
  };

  // Check if a specific action is allowed
  const isActionAllowed = (actionType = 'write') => {
    // Non-admin users don't have subscription restrictions
    if (user?.role !== 'Admin' && user?.role !== 'GroupAdmin') return true;
    
    // SuperAdmin always allowed
    if (user?.role === 'SuperAdmin') return true;

    // Read actions always allowed
    if (actionType === 'read') return true;

    // Write actions blocked when expired (but allowed during grace)
    if (actionType === 'write') {
      return !isExpired;
    }

    return true;
  };

  // Get warning message based on status
  const getWarningMessage = () => {
    if (isExpired) {
      return {
        type: 'error',
        title: 'Subscription Expired - READ ONLY MODE',
        message: 'Your subscription has expired. You can view data but cannot make any changes. Please renew to restore full access.'
      };
    }
    
    if (isGracePeriod) {
      return {
        type: 'warning',
        title: 'Grace Period Active',
        message: `Your subscription has expired. ${daysRemaining} day(s) left to renew before read-only mode.`
      };
    }
    
    if (daysRemaining !== null && daysRemaining <= 7) {
      return {
        type: 'info',
        title: 'Subscription Expiring Soon',
        message: `Your subscription expires in ${daysRemaining} day(s). Please renew to avoid interruption.`
      };
    }
    
    return null;
  };

  const value = {
    subscriptionStatus,
    isLoading,
    error,
    isExpired,
    isGracePeriod,
    isActive,
    canWrite,
    daysRemaining,
    refreshStatus,
    isActionAllowed,
    getWarningMessage,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    // Return default values if not wrapped in provider (for non-admin pages)
    return {
      subscriptionStatus: null,
      isLoading: false,
      error: null,
      isExpired: false,
      isGracePeriod: false,
      isActive: true,
      canWrite: true,
      daysRemaining: null,
      refreshStatus: () => {},
      isActionAllowed: () => true,
      getWarningMessage: () => null,
    };
  }
  return context;
}

export default SubscriptionContext;
